import db from "../db/database";
import { fetchFromBusinessCentral } from "../utils/businessCentralUtils";
import { isOnline } from "./syncService";
import { anotarError } from "./syncLog";

/**
 * Divisas de Business Central, con su tipo de cambio vigente. Todos los países.
 *
 * Lee el servicio OData `Divisas` (tabla Currency de BC). El tipo de cambio lo
 * actualiza BC a diario, así que se lee en cada sincronización completa --que
 * corre al iniciar sesión y en cada intervalo--: con eso queda garantizada al
 * menos una lectura por día. Son pocas filas, no hace falta lectura incremental.
 */

/** El nombre de la tabla en Dexie y de la entidad en `syncMetadata`. */
export const ENTIDAD = "divisas";

/** El servicio web de Business Central. */
export const SERVICIO = "Divisas";

const texto = (valor) => String(valor ?? "").trim();

// Los tipos de cambio (ExchangeRateAmt, CurrencyFactor) no son importes: se
// guardan tal cual llegan, sin roundMoney, para no perder precisión.
const numero = (valor) => {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
};

// Los campos se mapean uno por uno, sin spread: si BC renombra uno, que falte a
// la vista y no que se cuele con otro nombre.
export const mapDivisaFromBC = (divisa) => ({
  Code: texto(divisa?.Code),
  Description: texto(divisa?.Description),
  SUNAT_Document: texto(divisa?.SUNAT_Document),
  ISO_Code: texto(divisa?.ISO_Code),
  ISO_Numeric_Code: texto(divisa?.ISO_Numeric_Code),
  Symbol: texto(divisa?.Symbol),
  CurrencySymbolPosition: texto(divisa?.CurrencySymbolPosition),
  ExchangeRateDate: divisa?.ExchangeRateDate || null,
  ExchangeRateAmt: numero(divisa?.ExchangeRateAmt),
  CurrencyFactor: numero(divisa?.CurrencyFactor),
  EMU_Currency: Boolean(divisa?.EMU_Currency),
  Amount_Rounding_Precision: numero(divisa?.Amount_Rounding_Precision),
  Amount_Decimal_Places: texto(divisa?.Amount_Decimal_Places),
  Invoice_Rounding_Precision: numero(divisa?.Invoice_Rounding_Precision),
  Invoice_Rounding_Type: texto(divisa?.Invoice_Rounding_Type),
  Unit_Amount_Rounding_Precision: numero(divisa?.Unit_Amount_Rounding_Precision),
  Unit_Amount_Decimal_Places: texto(divisa?.Unit_Amount_Decimal_Places),
  Appln_Rounding_Precision: numero(divisa?.Appln_Rounding_Precision),
  Max_VAT_Difference_Allowed: numero(divisa?.Max_VAT_Difference_Allowed),
  VAT_Rounding_Type: texto(divisa?.VAT_Rounding_Type),
  Payment_Tolerance_Percent: numero(divisa?.Payment_Tolerance_Percent),
  Max_Payment_Tolerance_Amount: numero(divisa?.Max_Payment_Tolerance_Amount),
  Last_Date_Adjusted: divisa?.Last_Date_Adjusted || null,
  Last_Date_Modified: divisa?.Last_Date_Modified || null,
  lastSynced: new Date(),
});

export const syncDivisas = async () => {
  if (!isOnline()) {
    return false;
  }

  try {
    const divisas = await fetchFromBusinessCentral(SERVICIO);

    const filas = (Array.isArray(divisas) ? divisas : [])
      .map(mapDivisaFromBC)
      .filter((fila) => fila.Code);

    // Se reemplaza la tabla sólo después de leer bien: un fallo de red a mitad
    // no debe dejar al terminal sin tipos de cambio.
    await db.transaction("rw", db.divisas, async () => {
      await db.divisas.clear();
      if (filas.length) {
        await db.divisas.bulkAdd(filas);
      }
    });

    return true;
  } catch (error) {
    anotarError("divisas", "falló la lectura", error);
    return false;
  }
};

/** La divisa por su código de BC (ej. "USD"), o null si no está. */
export const getDivisa = async (code) => {
  const codigo = texto(code);
  if (!codigo) return null;

  return (await db.divisas.where("Code").equals(codigo).first()) || null;
};

export const getAllDivisas = async () => db.divisas.toArray();

/**
 * La moneda del país (countries.js), siempre con la forma de un registro de
 * `divisas` (Code, Description, Symbol, ...).
 *
 * BC no suele traer la moneda local en `Divisas` (es la divisa de la empresa,
 * no una extranjera), así que si no está se arma desde la configuración del
 * país, con tipo de cambio 1. Si está, se usa ese registro y sólo se completan
 * desde la configuración los textos que BC dejó vacíos (ej. Symbol: "").
 *
 * Devuelve null sólo si el país no tiene moneda configurada.
 */
export const getDivisaDelPais = async (country) => {
  const codigo = texto(country?.currency);
  if (!codigo) return null;

  const deBC = await getDivisa(codigo);
  const desdeConfiguracion = mapDivisaFromBC({
    Code: codigo,
    Description: country?.currencyName || codigo,
    Symbol: country?.currencySymbol,
    CurrencySymbolPosition: country?.currencyPosition === "suffix" ? "After Amount" : "Before Amount",
    ExchangeRateAmt: 1,
    CurrencyFactor: 1,
  });

  if (!deBC) return desdeConfiguracion;

  return {
    ...deBC,
    Description: deBC.Description || desdeConfiguracion.Description,
    Symbol: deBC.Symbol || desdeConfiguracion.Symbol,
    CurrencySymbolPosition: deBC.CurrencySymbolPosition || desdeConfiguracion.CurrencySymbolPosition,
  };
};
